<template>
  <div class="range-slider" ref="slider">
    <div class="slider-track"></div>
    <div
      class="slider-range"
      :style="{
        left: leftPercent + '%',
        width: rangePercent + '%'
      }"
    ></div>
    <div class="absolute top-0 left-0 text-white font-bold text-sm pt-[72px] whitespace-nowrap">
      {{ $t("level_from_double_dot") }} {{ localMin.toFixed(2) }}
    </div>
    <div class="absolute top-0 right-0 text-white font-bold text-sm pt-[72px] whitespace-nowrap">
      {{ $t("level_to_double_dot") }} {{ localMax.toFixed(2) }}
    </div>

    <div v-if="showControls">
      <div 
        class="thumb thumb-left"
        :style="{ left: leftPercent + '%' }"
        data-thumb="min"
        ref="thumbMin"
        @pointerdown.prevent="onStart"
      ></div>

      <div
        class="thumb thumb-right"
        :style="{ left: rightPercent + '%' }"
        data-thumb="max"
        ref="thumbMax"
        @pointerdown.prevent="onStart"
      ></div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";

export default defineComponent({
  name: "DoubleRangeSlider",
  props: {
    min: { type: Number, required: true, default: 0 },
    max: { type: Number, required: true, default: 100 },
    showControls: { type: Boolean, required: false, default: true },
    modelValue: { type: Object as PropType<[number, number]>, required: true },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      localMin: this.modelValue[0],
      localMax: this.modelValue[1],
      dragging: null as "min" | "max" | null,
      sliderWidth: 0,
      sliderLeft: 0,
    };
  },
  watch: {
    modelValue: {
      handler(newVal) {
        this.localMin = newVal[0];
        this.localMax = newVal[1];
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    leftPercent(): number {
      return ((this.localMin - this.min) / (this.max - this.min)) * 100;
    },
    rightPercent(): number {
      return ((this.localMax - this.min) / (this.max - this.min)) * 100;
    },
    rangePercent(): number {
      return this.rightPercent - this.leftPercent;
    },
  },
  methods: {
    onStart(e: PointerEvent) {
      const target = (e.target as HTMLElement).closest(".thumb") as HTMLElement | null;
      if (!target) return;

      const thumb = target.dataset.thumb as "min" | "max";
      this.dragging = thumb;

      const slider = this.$refs.slider as HTMLElement;
      const rect = slider.getBoundingClientRect();
      this.sliderWidth = rect.width;
      this.sliderLeft = rect.left;

      window.addEventListener("pointermove", this.onMove, { passive: false });
      window.addEventListener("pointerup", this.onEnd);
    },
    onMove(e: PointerEvent) {
      if (!this.dragging) return;
      e.preventDefault();

      let clientX = e.clientX;

      let percent = ((clientX - this.sliderLeft) / this.sliderWidth) * 100;
      percent = Math.max(0, Math.min(100, percent));

      const value = this.min + ((this.max - this.min) * percent) / 100;

      if (this.dragging === "min") {
        this.localMin = Math.min(value, this.localMax);
      } else {
        this.localMax = Math.max(value, this.localMin);
      }

      const min = Number(this.localMin.toFixed(2));
      const max = Number(this.localMax.toFixed(2));
      this.$emit("update:modelValue", [min, max]);
    },
    onEnd() {
      this.dragging = null;
      window.removeEventListener("pointermove", this.onMove);
      window.removeEventListener("pointerup", this.onEnd);
    },
  },
});
</script>

<style scoped>
.range-slider {
  position: relative;
  width: 100%;
  height: 90px;
  user-select: none;
  touch-action: none; 
}

.slider-track {
  position: absolute;
  top: 45px;
  left: 0;
  right: 0;
  height: 12px;
  background: #555;
  border-radius: 16px;
}

.slider-range {
  position: absolute;
  top: 45px;
  height: 12px;
  border-radius: 16px;
  background: linear-gradient(103.18deg, var(--color1) 20.29%, var(--color2) 97.77%);
}

.thumb {
  position: absolute;
  top: 39px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--color2);
  border: 2px solid var(--color2);
  cursor: pointer;
  transform: translateX(-50%);
  transition: transform 0.1s;
}

.thumb:hover {
  transform: translateX(-50%) scale(1.1);
}
</style>